// Photographed tree pins for the forests map - founder decision 2026-09-18
// (Option 2), overriding the 2026-08-31 standing entry: the 10,000 cap stays
// as a TOTAL budget but is split per project (4 x 2,500) instead of one
// unfiltered request that returned whichever 10,000 rows GeoServer happened to
// order first (all Mizan, never Limu - see PROGRESS.md 2026-09-17).
import { deadPhotoCqlExclusion } from '../components/Project/treeOrder';
import { fetchGeoServer } from './api';
import { treePinEnvelopeSchema, treePinFeatureSchema, type TreePinFeature } from './schemas';

export const TREE_PROJECTS = [
  'Limu Outgrowers',
  'BAC Farm',
  'Mizan 1 Ingrowers',
  'Dark Roast',
] as const;
export type TreeProject = (typeof TREE_PROJECTS)[number];

export const TREE_PINS_PER_PROJECT = 2500;

// Built in code, not read from VITE_TREES_PLANTED_LAYER, so the cap and the
// filter cannot be changed by whatever .env sat on the build machine (§5).
// The env-driven read is deprecated in place (§2), see queries.ts.
export const TREES_LAYER_PATH =
  '/ows?service=WFS&version=1.0.0&request=GetFeature&typeName=geonode%3Aall_trees_combined&outputFormat=application%2Fjson';

// Founder decision 2026-09-18 (second): upstream stores '' rather than NULL for
// a missing photo, so "IS NOT NULL" alone let 1,984 unusable Dark Roast rows
// through; "<> ''" makes that request return zero rows, which is correct.
//
// Founder decision 2026-09-22 (trees-4): also EXCLUDE the known-dead photo
// patterns upstream (deadPhotoCqlExclusion, sourced from treeOrder.ts
// PHOTO_URL_PATTERNS) so the 2,500-per-project budget is spent on trees whose
// photos actually render, not on Mizan `trees_*.JPG` 404s or the `bac_tree`
// bucket. TEMPORARY - remove the extra clause (and the pattern list) once
// Fairchain restores the files (see PROGRESS Q36).
export const treePinsCqlFilter = (project: TreeProject): string => {
  const dead = deadPhotoCqlExclusion();
  return `farmer_group='${project}' AND photo IS NOT NULL AND photo <> ''${dead ? ` AND ${dead}` : ''}`;
};

// Founder decision 2026-09-22 (trees-4): request the NEWEST planted first, so the
// per-project cap keeps the most recent live-photo trees rather than the
// lowest-id (oldest) ones. Verified 2026-09-22 that Fairchain's GeoServer honours
// WFS sortBy on this layer (planting_date descending returns newest first), so no
// date-window fallback is needed. GeoServer's WFS 1.0.0 sort syntax is
// "<field> D" for descending.
export const TREES_SORT_BY = 'planting_date D';

export interface ProjectPinStats {
  returned: number;
  matched: number;
  dropped: number;
  // Founder decision 2026-09-18 (second): each project request fails on its
  // own. `failed` carries the error message so the console counts line and
  // any future UI can say WHICH project is missing rather than hiding it.
  failed?: string;
}

// Structurally compatible with the legacy `Layer` interface in queries.ts so
// getTransformedData and the tree popup keep working unchanged.
export interface TreePinCollection {
  type: 'FeatureCollection';
  features: TreePinFeature[];
  numberMatched: number;
  numberReturned: number;
  totalFeatures: number;
  crs: { type: string; properties: { name: string } };
  timeStamp: string;
  // Per-project accounting so the console alone answers "how many pins did
  // each project contribute and how many rows were thrown away" (§8).
  perProject: Record<TreeProject, ProjectPinStats>;
}

interface ProjectResult extends ProjectPinStats {
  project: TreeProject;
  features: TreePinFeature[];
  crs?: TreePinCollection['crs'];
  timeStamp?: string;
}

// GeoServer always answers EPSG:4326 for this layer; used only if the
// envelope carries no crs (it is not something we render).
const DEFAULT_CRS: TreePinCollection['crs'] = {
  type: 'name',
  properties: { name: 'urn:ogc:def:crs:EPSG::4326' },
};

// Validate row by row: a bad coordinate drops that ONE row (counted and logged
// with the project and the first issue) instead of hiding the whole project.
export const validateTreePinFeatures = (
  project: TreeProject,
  rows: unknown[],
): { features: TreePinFeature[]; dropped: number } => {
  const features: TreePinFeature[] = [];
  let dropped = 0;
  let firstIssue: string | undefined;
  for (const row of rows) {
    const result = treePinFeatureSchema.safeParse(row);
    if (result.success) {
      features.push(result.data);
    } else {
      dropped += 1;
      firstIssue ??= result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ');
    }
  }
  if (dropped > 0) {
    console.warn(
      `[boundary] all_trees_combined (${project}): dropped ${dropped} of ${rows.length} rows failing validation. First issue: ${firstIssue}`,
    );
  }
  return { features, dropped };
};

const fetchProject = async (project: TreeProject): Promise<ProjectResult> => {
  const raw = await fetchGeoServer<unknown>(TREES_LAYER_PATH, {
    maxFeatures: TREE_PINS_PER_PROJECT,
    CQL_FILTER: treePinsCqlFilter(project),
    sortBy: TREES_SORT_BY,
  });
  const envelope = treePinEnvelopeSchema.safeParse(raw);
  if (!envelope.success) {
    console.error(
      `[boundary] all_trees_combined (${project}): envelope is not a FeatureCollection`,
      envelope.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })),
    );
    throw new Error(`all_trees_combined (${project}): response failed schema validation`);
  }
  const { features, dropped } = validateTreePinFeatures(project, envelope.data.features);
  return {
    project,
    features,
    returned: features.length,
    matched: envelope.data.numberMatched ?? envelope.data.totalFeatures ?? features.length,
    dropped,
    // null on an empty collection (real upstream shape) - treated as absent.
    crs: envelope.data.crs ?? undefined,
    timeStamp: envelope.data.timeStamp,
  };
};

// Four parallel requests, merged into one FeatureCollection. Founder decision
// 2026-09-18 (second, replacing the earlier fail-whole rule): each project
// fails independently. Whatever loaded is drawn, a warning names each project
// that did not, and the console counts line says so. Only all four failing
// leaves the layer empty (thrown, so the query reaches its error state).
export const fetchTreePinsByProject = async (): Promise<TreePinCollection> => {
  const settled = await Promise.allSettled(TREE_PROJECTS.map(fetchProject));
  const perProject = {} as TreePinCollection['perProject'];
  const features: TreePinFeature[] = [];
  const results: ProjectResult[] = [];
  const failedProjects: TreeProject[] = [];
  let matched = 0;
  settled.forEach((outcome, i) => {
    const project = TREE_PROJECTS[i];
    if (outcome.status === 'rejected') {
      const reason =
        outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason);
      failedProjects.push(project);
      perProject[project] = { returned: 0, matched: 0, dropped: 0, failed: reason };
      console.warn(`[trees] ${project}: request FAILED, no pins drawn for this project: ${reason}`);
      return;
    }
    const r = outcome.value;
    results.push(r);
    perProject[project] = { returned: r.returned, matched: r.matched, dropped: r.dropped };
    features.push(...r.features);
    matched += r.matched;
  });
  if (failedProjects.length === TREE_PROJECTS.length) {
    throw new Error(
      `all_trees_combined: all ${TREE_PROJECTS.length} project requests failed - no tree pins available`,
    );
  }
  // Diagnostic stats line (not an error): the founder asked for per-project
  // photographed-pin counts, including failures, to be readable from the
  // console alone.
  // eslint-disable-next-line no-console
  console.info(
    `[trees] counts: ${TREE_PROJECTS.map((p) => {
      const s = perProject[p];
      return s.failed
        ? `${p} FAILED (0 drawn)`
        : `${p} ${s.returned} returned (cap ${TREE_PINS_PER_PROJECT}), ${s.matched} photographed upstream, ${s.dropped} dropped`;
    }).join(' | ')}${failedProjects.length ? ` | MISSING: ${failedProjects.join(', ')}` : ''}`,
  );
  return {
    type: 'FeatureCollection',
    features,
    numberMatched: matched,
    numberReturned: features.length,
    totalFeatures: matched,
    crs: results.find((r) => r.crs)?.crs ?? DEFAULT_CRS,
    timeStamp: results.find((r) => r.timeStamp)?.timeStamp ?? new Date().toISOString(),
    perProject,
  };
};
